// MailingClassStep: choose mailing class for PostGrid items (US only)
import React, { useEffect, useState } from 'react';

import { WizardStep } from '@/components/postgrid/wizard';
// Front-end API type generated via OpenAPI
import type { PostGridMailingClass } from '@/services/api/generated/postgridService';

export type MailingClass =
  | 'first_class'
  | 'express'
  | 'certified'
  | 'certified_return_receipt'
  | 'registered';

export interface MailingOption {
  value: MailingClass;
  label: string;
  description: string;
  deliveryTime: string;
  features: string[];
  disabled?: boolean;
}

// PostGrid mailing class options (US-focused)
export const MAILING_OPTIONS: MailingOption[] = [
  {
    value: 'first_class',
    label: 'First Class',
    description:
      'Default PostGrid mail service with suitable carrier selection',
    deliveryTime: '3-5 business days',
    features: ['Standard delivery'],
  },
  {
    value: 'certified',
    label: 'Certified Mail',
    description: 'Temporarily unavailable',
    deliveryTime: '3-5 business days',
    features: ['Delivery confirmation', 'Proof of mailing'],
    disabled: true,
  },
  {
    value: 'certified_return_receipt',
    label: 'Certified + Return Receipt',
    description: 'Temporarily unavailable',
    deliveryTime: '3-5 business days',
    features: ['Delivery confirmation', 'Return receipt'],
    disabled: true,
  },
  {
    value: 'express',
    label: 'Express Mail',
    description: 'Expedited delivery through selected carrier',
    deliveryTime: '1-3 business days',
    features: ['Expedited delivery'],
  },
  {
    value: 'registered',
    label: 'Registered Mail',
    description: 'Enhanced security mail with chain of custody tracking',
    deliveryTime: '5-10 business days',
    features: ['Chain of custody'],
  },
];

interface MailingClassStepProps {
  wizardData?: {
    mailing_class?: MailingClass;
  };
  updateStepData?: (data: Record<string, unknown>) => void;
  markStepComplete?: (isValid: boolean) => void;
}

export const MailingClassStep: React.FC<MailingClassStepProps> = ({
  wizardData = {},
  updateStepData,
  markStepComplete,
}) => {
  const [selected, setSelected] = useState<MailingClass>(
    (wizardData.mailing_class as MailingClass) || 'first_class',
  );

  const handleChange = (value: MailingClass) => {
    setSelected(value);
    updateStepData?.({ mailing_class: value });
    markStepComplete?.(true);
  };

  // Ensure initialization with tracking-enabled default
  useEffect(() => {
    if (!wizardData.mailing_class) {
      // Default to first_class to guarantee full tracking
      updateStepData?.({
        mailing_class: 'first_class' satisfies PostGridMailingClass,
      });
      markStepComplete?.(true);
    }
  }, [wizardData.mailing_class, updateStepData, markStepComplete]);

  return (
    <WizardStep
      title="Mailing Class"
      description="Choose your delivery speed and service level."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {MAILING_OPTIONS.map((option) => (
          <div
            key={option.value}
            onClick={() => !option.disabled && handleChange(option.value)}
            className={`
              relative rounded-lg border-2 p-4 transition-all
              ${option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:shadow-md'}
              ${
                selected === option.value
                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                  : option.disabled
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }
            `}
            role="button"
            tabIndex={option.disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (!option.disabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleChange(option.value);
              }
            }}
            aria-pressed={selected === option.value}
            aria-disabled={option.disabled}
          >
            {/* Selection indicator */}
            <div className="absolute right-3 top-3">
              <div
                className={`
                  h-4 w-4 rounded-full border-2 transition-all
                  ${
                    selected === option.value
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }
                `}
              >
                {selected === option.value && (
                  <div className="h-full w-full rounded-full bg-white scale-50" />
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-gray-900">{option.label}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {option.description}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-700">
                    {option.deliveryTime}
                  </span>
                </div>

                {option.features.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {option.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </WizardStep>
  );
};
